import React, { useEffect, useMemo, useState } from "react";
import { PageSectionHeader } from "../components/subcomponents/PageSectionHeader";
import { apiGet } from "../utils/api";
import { PageSkeleton } from "../components/PageSkeleton";
import { FiClock, FiMapPin, FiDownload, FiBook, FiGrid, FiFile } from "react-icons/fi";
import { WeeklyScheduleGrid } from "../components/schedules/WeeklyScheduleGrid";
import { getScheduleDisplayMode, getScheduleGrid } from "../utils/scheduleGrid";
import "../styles/schedules.scss";

const ClockI = FiClock as any;
const MapI = FiMapPin as any;
const DownloadI = FiDownload as any;
const BookI = FiBook as any;
const GridI = FiGrid as any;
const FileI = FiFile as any;

const PROMOTIONS = ["L0", "L1", "L2", "L3", "M1", "M2"];

export const SchedulesPage: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [faculties, setFaculties] = useState<any[]>([]);
  const [filters, setFilters] = useState<any>({ type: "cours", facultyId: "", promotion: "" });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    apiGet("/faculties").then((d) => setFaculties(d.rows)).catch(() => { });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params: any = {};
    if (filters.type) params.type = filters.type;
    if (filters.facultyId) params.facultyId = filters.facultyId;
    if (filters.promotion) params.promotion = filters.promotion;
    apiGet("/schedules", params)
      .then((d) => {
        const rows = d.rows || [];
        setItems(rows);
        setSelectedId((prev) => {
          if (prev && rows.some((r: any) => r.id === prev)) return prev;
          return rows[0]?.id ?? null;
        });
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const selected = useMemo(
    () => items.find((s) => s.id === selectedId) || items[0] || null,
    [items, selectedId]
  );

  const gridData = selected ? getScheduleGrid(selected) : null;
  const displayMode = selected ? getScheduleDisplayMode(selected) : "document";
  const isPdf = (url: string) => /\.pdf(\?|$)/i.test(url);

  return (
    <>
      <div className="page-content padding-x is-layout-constrained schedules-page">
        <PageSectionHeader
          eyebrow="Planning"
          title="Horaires des cours & examens"
          lead="Consultez le tableau hebdomadaire par promotion ou téléchargez le document officiel (PDF / image)."
          align="start"
        />

        <div data-testid="schedules-filters" className="schedules-filters">
          <select
            data-testid="filter-type"
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            className="schedules-filters__select"
          >
            <option value="">Tous les types</option>
            <option value="cours">Cours</option>
            <option value="examen">Examens</option>
          </select>
          <select
            data-testid="filter-faculty"
            value={filters.facultyId}
            onChange={(e) => setFilters({ ...filters, facultyId: e.target.value })}
            className="schedules-filters__select"
          >
            <option value="">Toutes les facultés</option>
            {faculties.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <select
            data-testid="filter-promotion"
            value={filters.promotion}
            onChange={(e) => setFilters({ ...filters, promotion: e.target.value })}
            className="schedules-filters__select"
          >
            <option value="">Toutes promotions</option>
            {PROMOTIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <PageSkeleton />
        ) : items.length === 0 ? (
          <p className="schedules-empty">Aucun horaire publié pour ces critères.</p>
        ) : (
          <div className="schedules-layout">
            <aside className="schedules-sidebar" data-testid="schedules-list">
              <h3 className="schedules-sidebar__title">Horaires publiés</h3>
              <ul className="schedules-sidebar__list">
                {items.map((s) => {
                  const hasGrid = getScheduleDisplayMode(s) === "grid" && !!getScheduleGrid(s);
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        data-testid={`schedule-tab-${s.id}`}
                        className={`schedules-sidebar__item${selected?.id === s.id ? " is-active" : ""}`}
                        onClick={() => setSelectedId(s.id)}
                      >
                        <span className="schedules-sidebar__badges">
                          <span className={`schedules-badge schedules-badge--${s.type}`}>{s.type}</span>
                          {hasGrid ? <GridI size={12} /> : s.fileUrl ? <FileI size={12} /> : null}
                        </span>
                        <strong>{s.title}</strong>
                        <span className="schedules-sidebar__meta">
                          {s.faculty?.name} · {s.promotion}
                          {s.academicYear && ` · ${s.academicYear}`}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>

            <main className="schedules-main" data-testid="schedule-viewer">
              {selected && (
                <>
                  <div className="schedules-main__head">
                    <div>
                      <span className={`schedules-badge schedules-badge--${selected.type}`}>{selected.type}</span>
                      <h2 className="schedules-main__title">{selected.title}</h2>
                      <div className="schedules-main__meta">
                        <BookI size={14} /> {selected.faculty?.name}
                        {selected.filiere?.name && <> · {selected.filiere.name}</>}
                        {selected.promotion && <> · {selected.promotion}</>}
                      </div>
                      {selected.startDate && (
                        <div className="schedules-main__meta">
                          <ClockI size={14} />
                          {" "}
                          {new Date(selected.startDate).toLocaleString("fr-FR", { dateStyle: "long" })}
                          {selected.endDate &&
                            ` — ${new Date(selected.endDate).toLocaleString("fr-FR", { dateStyle: "long" })}`}
                        </div>
                      )}
                      {selected.location && (
                        <div className="schedules-main__meta">
                          <MapI size={14} /> {selected.location}
                        </div>
                      )}
                    </div>
                    {selected.fileUrl && (
                      <a
                        href={selected.fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`schedule-download-${selected.id}`}
                        className="schedules-download-btn"
                      >
                        <DownloadI size={16} />
                        {isPdf(selected.fileUrl) ? "Télécharger le PDF" : "Voir le document"}
                      </a>
                    )}
                  </div>

                  {displayMode === "grid" && gridData ? (
                    <div className="schedules-grid-wrap">
                      <WeeklyScheduleGrid
                        data={{
                          ...gridData,
                          facultyName: gridData.facultyName || selected.faculty?.name,
                          weekLabel:
                            gridData.weekLabel ||
                            (selected.startDate && selected.endDate
                              ? `HORAIRE DES COURS POUR LA SEMAINE DU ${formatWeekRange(selected.startDate, selected.endDate)}`
                              : selected.title),
                        }}
                      />
                    </div>
                  ) : selected.fileUrl ? (
                    <div className="schedules-document">
                      {isPdf(selected.fileUrl) ? (
                        <iframe
                          title={selected.title}
                          src={selected.fileUrl}
                          className="schedules-document__iframe"
                        />
                      ) : (
                        <img src={selected.fileUrl} alt={selected.title} className="schedules-document__image" />
                      )}
                    </div>
                  ) : (
                    <p className="schedules-empty">Aucun tableau ni document associé à cet horaire.</p>
                  )}

                  {selected.description && displayMode === "document" && !selected.description.startsWith("__SCHEDULE_GRID__") && (
                    <p className="schedules-notes">{selected.description}</p>
                  )}
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </>
  );
};

function formatWeekRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };
  return `${s.toLocaleDateString("fr-FR", opts).toUpperCase()} AU ${e.toLocaleDateString("fr-FR", opts).toUpperCase()}`;
}
